import type {
  DataGridColumnOrderState,
  DataGridColumnPinningState,
  DataGridColumnSizingState,
  DataGridColumnVisibilityState,
  DataGridFilterState,
  DataGridSortingState,
} from '@ibsys/kit';
import { useMemo } from 'react';
import { useExpertReportsResultShapes } from '@/entities/expert-report';
import type { SearchDialogsPage } from '@/entities/search';
import type {
  DataGridColumnActions,
  DataGridColumnSettings,
  DataGridColumnStateProps,
  ExpertReportSettings,
} from '@/entities/settings';
import type { DialogMainColumn } from './types';
import { useDialogColumnsData } from './useDialogColumnsData';
import { useDialogColumnsState } from './useDialogColumnsState';
import { useDialogDataGridColumns } from './useDialogDataGridColumns';
import { useDialogSearchParams } from './useDialogSearchParams';
import type { ContentSearchColumnOptions } from './contentSearchColumnHelpers';
import { useEffectiveColumnOrder } from './useEffectiveColumnOrder';

/** Параметры подсистемы колонок списка диалогов */
type Params = {
  /** Настройки таблицы (персистенция колонок) */
  settings: DataGridColumnSettings;
  /** Экшены таблицы */
  actions: DataGridColumnActions;
  /** Фильтры грида */
  filters: DataGridFilterState;
  /** Сортировка грида */
  sorting: DataGridSortingState;
  /** Колонки поиска по содержимому (появляются при активном режиме) */
  contentSearch: ContentSearchColumnOptions;
  /** Отсекает id колонок поиска по содержимому из пользовательского порядка */
  filterUserColumnOrder: (columnOrder: string[]) => string[];
  /** Ставит пару колонок поиска по содержимому в начало порядка */
  getEffectiveColumnOrder: (userColumnOrder: string[]) => string[];
  /** Сохранённый в профиле состав основных колонок */
  mainColumns: string[] | undefined;
  /** Записывает состав основных колонок в настройки профиля */
  onMainColumnsChange: (columns: DialogMainColumn[]) => void;
};

/** Колонки таблицы диалогов и параметры запроса из их видимости */
type Result = {
  /** Определения колонок DataGrid */
  columns: ReturnType<typeof useDialogDataGridColumns>;
  /** Данные колонок ещё грузятся (атрибуты/отчёты) */
  isLoading: boolean;
  /** Стейт колонок готов */
  isReady: boolean;
  /** Пропы колонок грида с эффективным порядком */
  gridColumnProps: DataGridColumnStateProps;
  /** Эффективный порядок колонок */
  columnOrder: DataGridColumnOrderState;
  /** Закрепление колонок */
  columnPinning: DataGridColumnPinningState;
  /** Видимость колонок с явным значением по каждой известной колонке */
  columnVisibility: DataGridColumnVisibilityState;
  /** Ширина колонок; записи нет — колонка в системной ширине */
  columnSizing: DataGridColumnSizingState;
  /** Пути видимых атрибутов */
  visibleAttributePaths: string[];
  /** Видимые основные колонки */
  visibleMainColumns: DialogMainColumn[];
  /** Параметры видимых экспертных заключений */
  expertReportSearchParams: ExpertReportSettings[];
  /** Собранный `dialogsPage` для запроса списка */
  searchDialogsPage: SearchDialogsPage;
};

/**
 * Подсистема колонок списка диалогов: строит определения колонок из данных бэкенда, ведёт стейт их
 * видимости/порядка/закрепления с персистенцией, накладывает эффективный порядок в режиме поиска по
 * содержимому и собирает из текущей видимости параметры запроса списка (атрибуты, основные колонки,
 * экспертные заключения).
 */
export const useDialogColumns = ({
  settings,
  actions,
  filters,
  sorting,
  contentSearch,
  filterUserColumnOrder,
  getEffectiveColumnOrder,
  mainColumns,
  onMainColumnsChange,
}: Params): Result => {
  const { defaultVisibility, allAttributes, allReports, blocksByReportId, isLoading } = useDialogColumnsData();

  const { gridColumnProps, columnVisibility, isReady } = useDialogColumnsState(
    settings,
    actions,
    filters,
    sorting,
    defaultVisibility,
    isLoading,
  );

  const { effectiveColumnOrder, effectiveGridColumnProps } = useEffectiveColumnOrder({
    gridColumnProps,
    filterUserColumnOrder,
    getEffectiveColumnOrder,
  });

  const { visibleAttributePaths, visibleMainColumns, expertReportSearchParams, searchDialogsPage } =
    useDialogSearchParams({
      allAttributes,
      allReports,
      blocksByReportId,
      columnVisibility,
      isReady,
      mainColumns,
      onMainColumnsChange,
    });

  // Вид результата (справочник значений, размерность) нужен колонкам заключения до того, как
  // придут сами результаты: им он задаёт и подачу ячейки, и вид отбора. Берётся он из деревьев
  // элементов, уже загруженных для всех заключений, — отдельных запросов не добавляет.
  const visibleReportIds = useMemo(
    () => expertReportSearchParams.map((report) => report.id),
    [expertReportSearchParams],
  );
  const resultShapesByReportId = useExpertReportsResultShapes(visibleReportIds);

  const columns = useDialogDataGridColumns({
    allAttributes,
    allReports,
    blocksByReportId,
    resultShapesByReportId,
    contentSearch,
  });

  return {
    columns,
    isLoading,
    isReady,
    gridColumnProps: effectiveGridColumnProps,
    columnOrder: effectiveColumnOrder,
    columnPinning: gridColumnProps.columnPinning,
    columnVisibility,
    columnSizing: gridColumnProps.columnSizing,
    visibleAttributePaths,
    visibleMainColumns,
    expertReportSearchParams,
    searchDialogsPage,
  };
};
